import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown, ChevronUp, Check } from "lucide-react";
import { cn } from "@/utils";

interface Option {
  value: string;
  label: string;
}

interface SelectProps {
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  className?: string;
  defaultValue?: string;
  /** Controlled value; when undefined, component is uncontrolled. */
  value?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

// Heights mirror inputShell (sm=h-8, md=h-9, lg=h-11) so Select sits flush
// next to FormInput in modal grids with no pixel step.
const triggerHeight: Record<NonNullable<SelectProps["size"]>, string> = {
  sm: "h-8 text-xs px-3",
  md: "h-9 text-sm px-3.5",
  lg: "h-11 text-sm px-4",
};

// Radix Select forbids `value=""` on Item — using "" throws at render. Many
// of our filters semantically use "" for the "All / no filter" entry, so we
// map "" ↔ this sentinel internally and the consumer never sees it.
const EMPTY_TOKEN = "__select_empty__";
const toRadix = (v: string) => (v === "" ? EMPTY_TOKEN : v);
const fromRadix = (v: string) => (v === EMPTY_TOKEN ? "" : v);

/**
 * Select — Radix portal-based select. Content renders outside overflow:hidden
 * ancestors so it never gets clipped by card or sidebar stacking contexts.
 * Keeps the same {value, onChange, options, placeholder, size, disabled} API
 * as the replaced native <select> for consumer compat.
 *
 * Primitive routing:
 * - Select: short static option lists.
 * - SearchableSelect: longer lists where text filtering helps.
 * - Combobox: custom option rendering, creation, or domain-specific keyboard UX.
 */
const Select: React.FC<SelectProps> = ({
  options,
  placeholder = "Select an option",
  onChange,
  className,
  defaultValue,
  value,
  disabled = false,
  size = "md",
}) => {
  const isControlled = value !== undefined;

  return (
    <SelectPrimitive.Root
      value={isControlled ? toRadix(value ?? "") : undefined}
      defaultValue={!isControlled ? toRadix(defaultValue ?? "") : undefined}
      onValueChange={(v) => onChange(fromRadix(v))}
      disabled={disabled}
    >
      <SelectPrimitive.Trigger
        className={cn(
          "flex w-full items-center justify-between gap-2",
          "rounded-[8px] border border-[var(--border)] bg-[var(--card)]",
          "text-[var(--foreground)] transition-colors",
          "focus:outline-none focus:border-[var(--ring)] focus:shadow-[var(--shadow-focus-ring)]",
          "disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-[var(--muted)]",
          "data-[placeholder]:text-[var(--muted-foreground)]",
          triggerHeight[size],
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-4 shrink-0 text-[var(--muted-foreground)]" aria-hidden />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={cn(
            "z-[var(--z-dropdown)] overflow-hidden",
            "rounded-[var(--radius-lg)] border border-[var(--border)]",
            "bg-[var(--popover)] shadow-[var(--shadow-lg)]",
            "max-h-[var(--radix-select-content-available-height)]",
            "w-[var(--radix-select-trigger-width)] min-w-[8rem]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1"
          )}
        >
          <SelectPrimitive.ScrollUpButton className="flex items-center justify-center py-1 text-[var(--muted-foreground)]">
            <ChevronUp className="size-4" />
          </SelectPrimitive.ScrollUpButton>

          <SelectPrimitive.Viewport className="p-1">
            {options.map((opt) => (
              <SelectPrimitive.Item
                key={opt.value || EMPTY_TOKEN}
                value={toRadix(opt.value)}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center",
                  "rounded-md py-1.5 pl-2 pr-8 text-sm outline-none",
                  "text-[var(--foreground)]",
                  "focus:bg-[var(--accent)] focus:text-[var(--accent-foreground)]",
                  "data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                )}
              >
                <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
                <span className="absolute right-2 flex size-3.5 items-center justify-center">
                  <SelectPrimitive.ItemIndicator>
                    <Check className="size-3.5 text-[var(--primary)]" />
                  </SelectPrimitive.ItemIndicator>
                </span>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>

          <SelectPrimitive.ScrollDownButton className="flex items-center justify-center py-1 text-[var(--muted-foreground)]">
            <ChevronDown className="size-4" />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
};

export default Select;

// Named exports for consumers needing custom item rendering
export {
  SelectPrimitive as SelectParts,
};
